import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    FiArrowRight,
    FiCheckCircle,
    FiClock,
    FiFileText,
    FiLock,
    FiPlus,
    FiRefreshCw,
    FiShield,
    FiXCircle,
} from "react-icons/fi";

import { showTost } from "../../showTost";
import ThemedPageFrame from "./ThemedPageFrame";
import { MyContext } from "../../Context/MyProvider";

import {
    getXsrfTokenCookie,
    SHOP_API_TOKEN_STORAGE_KEY,
    SHOP_RECHARGE_ENDPOINTS,
    isUserLoggedIn,
    requireLogin,
} from "../../utils/auth";
import {
    openRechargeRazorpay,
    saveRechargePaymentStatus,
} from "../../utils/rechargePayment";
import "../../Style/style.css";

const getList = (data, key) => {
    if (Array.isArray(data?.[key])) return data[key];
    if (Array.isArray(data?.data?.[key])) return data.data[key];
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

const getErrorMessage = (error) => {
    const response = error?.response?.data;

    if (response?.msg) {
        try {
            const parsed =
                typeof response.msg === "string"
                    ? JSON.parse(response.msg)
                    : response.msg;

            return parsed?.msg || parsed?.message || response.msg;
        } catch {
            return response.msg;
        }
    }

    if (response?.message) return response.message;

    return error?.message || "Unable to complete the request.";
};

const getAuthConfig = () => {
    const token =
        getXsrfTokenCookie() ||
        localStorage.getItem(SHOP_API_TOKEN_STORAGE_KEY);

    return {
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    };
};

const firstValue = (object, keys = []) => {
    for (const key of keys) {
        const value = key
            .split(".")
            .reduce((current, part) => current?.[part], object);

        if (value !== undefined && value !== null && String(value).trim() !== "") {
            return value;
        }
    }

    return "";
};

const getP1Label = (operator = {}) =>
    firstValue(operator, [
        "p1_label",
        "param1_label",
        "parameter1_label",
        "customer_field_label",
        "customer_number_label",
        "input_label",
        "p1_name",
        "param1_name",
    ]);

const getP1Placeholder = (operator = {}) =>
    firstValue(operator, [
        "p1_placeholder",
        "param1_placeholder",
        "parameter1_placeholder",
        "customer_field_placeholder",
        "customer_number_placeholder",
        "input_placeholder",
    ]);

const getInitials = (name = "") => {
    const clean = String(name).trim();

    if (!clean) return "";

    const words = clean.split(/\s+/);

    if (words.length >= 2) {
        return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }

    return clean.slice(0, 2).toUpperCase();
};

const formatDate = (value) => {
    if (!value) return "Date unavailable";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getStatus = (value) =>
    String(value || "pending").trim().toLowerCase();

const getStatusMeta = (status) => {
    if (["success", "successful", "completed"].includes(status)) {
        return {
            label: "Successful",
            className: "success",
            icon: <FiCheckCircle />,
        };
    }

    if (["failed", "failure", "cancelled"].includes(status)) {
        return {
            label: "Failed",
            className: "failed",
            icon: <FiXCircle />,
        };
    }

    return {
        label: "Pending",
        className: "pending",
        icon: <FiClock />,
    };
};


const getHistoryText = (item = {}) =>
    [
        item?.operator_type,
        item?.service_name,
        item?.operator_name,
        item?.operator,
        item?.recharge_type,
        item?.type,
        item?.category_name,
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

const looksLikeBillHistory = (item = {}) => {
    const searchable = getHistoryText(item);

    if (
        searchable.includes("dth") ||
        searchable.includes("direct to home") ||
        item?.mobile_no ||
        item?.customer_id ||
        item?.subscriber_id
    ) {
        return false;
    }

    return Boolean(
        item?.customer_email ||
        item?.customer_number ||
        item?.customer_no ||
        item?.p1 ||
        searchable.includes("bill") ||
        searchable.includes("bbps")
    );
};

const PayBills = () => {
    const { setIsLoginShow, isLogin, BaseUrl } = useContext(MyContext);
    const navigate = useNavigate();
    const location = useLocation();

    const serviceId = location.state?.serviceId || new URLSearchParams(location.search).get("serviceId") || 2;

    const [operators, setOperators] = useState([]);
    const [plans, setPlans] = useState([]);
    const [history, setHistory] = useState([]);

    const [selectedOperator, setSelectedOperator] = useState(null);

    const [customerNumber, setCustomerNumber] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [amount, setAmount] = useState("");


    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const [showForm, setShowForm] = useState(false);
    const [showAllHistory, setShowAllHistory] = useState(false);

    const [paymentResponse, setPaymentResponse] = useState(null);

    const loadHistory = async () => {
        try {
            const response = await axios.get(
                SHOP_RECHARGE_ENDPOINTS.history,
                getAuthConfig()
            );

            setHistory(getList(response.data, "data").filter(looksLikeBillHistory));
        } catch {
            // History is supplementary; don't block bill payment UI.
        }
    };

    useEffect(() => {
        if (!isUserLoggedIn()) {
            setLoading(false);
            return;
        }

        const loadBillData = async () => {
            try {
                setError("");

                const config = getAuthConfig();

                const [
                    operatorResponse,
                    historyResponse,
                    plansResponse,
                ] = await Promise.all([
                    axios.get(
                        `https://shop.nabhiverse.com/api/v1/operator/list/${serviceId}`,
                        config
                    ),

                    axios.get(
                        SHOP_RECHARGE_ENDPOINTS.history,
                        config
                    ),

                    axios.get(
                        "https://shop.nabhiverse.com/api/v1/plans",
                        config
                    ),
                ]);
                const billOperators =
                    operatorResponse?.data?.response || [];

                const allPlans =
                    plansResponse?.data?.response || [];

                const availablePlans = allPlans.filter(
                    (plan) =>
                        String(plan?.service_id) ===
                        String(serviceId)
                );

                setOperators(billOperators);
                setPlans(availablePlans);

                setHistory(
                    getList(historyResponse.data, "data").filter(
                        looksLikeBillHistory
                    )
                );
            } catch (requestError) {
                setError(getErrorMessage(requestError));
            } finally {
                setLoading(false);
            }
        };

        loadBillData();
    }, [isLogin, serviceId]);


    const visibleHistory = showAllHistory
        ? history
        : history.slice(0, 5);
    const getHistoryOperator = (item) => {
        const historyOperatorCode = item?.operator_code;
        const historyOperatorId = item?.operator_id;

        return (
            operators.find(
                (operator) =>
                    String(operator?.operator_code) ===
                    String(historyOperatorCode)
            ) ||
            operators.find(
                (operator) =>
                    String(operator?.operator_id) ===
                    String(historyOperatorId)
            ) ||
            operators.find(
                (operator) =>
                    String(operator?.operator_id) ===
                    String(historyOperatorCode)
            )
        );
    };

    const resetPaymentForm = () => {
        setSelectedOperator(null);

        setCustomerNumber("");
        setCustomerEmail("");
        setAmount("");

        setPaymentResponse(null);
    };

    const handleNewBillPayment = () => {
        if (!requireLogin(setIsLoginShow, "/pay-bills")) return;

        resetPaymentForm();
        setShowForm(true);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };


    const handleOperatorSelect = (operator) => {
        setSelectedOperator(operator);
        setPaymentResponse(null);
    };
   const getOperatorPlans = (operator) => {
    if (!operator?.operator_id) return [];

    const planOperator = plans.find(
        (item) =>
            String(item?.service_id) === String(serviceId) &&
            String(item?.operator_id) ===
                String(operator.operator_id)
    );

    return Array.isArray(planOperator?.operator_recharge_plan)
        ? planOperator.operator_recharge_plan
        : [];
};
    const selectedOperatorPlans = getOperatorPlans(
        selectedOperator
    );
    const validateForm = () => {
        if (!selectedOperator) {
            return "Select a biller.";
        }

        if (!selectedOperator?.operator_code) {
            return "Selected biller does not have a valid operator code.";
        }

        if (!customerNumber.trim()) {
            return `${getP1Label(selectedOperator) || "P1"} is required.`;
        }

        if (!customerEmail.trim()) {
            return "Email address is required.";
        }

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailPattern.test(customerEmail.trim())) {
            return "Enter a valid email address.";
        }

        if (!amount || Number(amount) <= 0) {
            return "Enter a valid bill amount.";
        }

        return "";
    };

    const buildBillPayload = () => ({
        service_type: "bill_payment",
        p1: customerNumber.trim(),
        mobile_no: customerNumber.trim(),
        amount: Number(amount),
        p2: "",
        p3: "",
        customer_email: customerEmail.trim(),
        operator_code: String(selectedOperator?.operator_code || "").trim(), partner_request_id: String(Date.now()),
        user_var1: "",
        user_var2: "",
        user_var3: "",
    });

    const handlePayBill = async () => {
        if (!requireLogin(setIsLoginShow)) return;

        const validationMessage = validateForm();

        if (validationMessage) {
            showTost(validationMessage, "error");
            return;
        }

        const payload = buildBillPayload();

        setSubmitting(true);
        setPaymentResponse(null);

        try {
            const payment = await openRechargeRazorpay({
                baseUrl: BaseUrl,
                amount: payload.amount,
                description: `Bill payment for ${selectedOperator.operator_name}`,
                serviceLabel: "Bill Payment",
            });

            const response = await axios.post(
                SHOP_RECHARGE_ENDPOINTS.order,
                {
                    ...payload,
                    razorpay_payment_id: payment?.razorpay_payment_id,
                    razorpay_order_id: payment?.razorpay_order_id,
                    razorpay_signature: payment?.razorpay_signature,
                    payment_status: "success",
                },
                getAuthConfig()
            );

            const data = response?.data;

            if (data?.status === true) {
                setPaymentResponse(data);
                saveRechargePaymentStatus({
                    status: "success",
                    serviceLabel: "Bill Payment",
                    amount: payload.amount,
                    message: data?.message || "Bill payment request submitted successfully.",
                    requestId: data?.request_id || payload.partner_request_id,
                    razorpayOrderId: payment?.razorpay_order_id,
                    razorpayPaymentId: payment?.razorpay_payment_id,
                    retryPath: "/pay-bills",
                });

                showTost(
                    data?.message ||
                    "Bill payment request submitted successfully.",
                    "success"
                );

                await loadHistory();
                navigate("/recharge-payment-status?status=success");
            } else {
                const message =
                    data?.message ||
                    data?.msg ||
                    "Bill payment request failed.";

                throw new Error(message);
            }
        } catch (requestError) {
            const message = getErrorMessage(requestError);

            saveRechargePaymentStatus({
                status: "failed",
                serviceLabel: "Bill Payment",
                amount,
                message,
                retryPath: "/pay-bills",
            });

            showTost(message, "error");
            setPaymentResponse(null);
            navigate("/recharge-payment-status?status=failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handlePayAgain = (item) => {
        if (!requireLogin(setIsLoginShow, "/pay-bills")) return;

        const operatorCode = item?.operator_code || "";

        const operator = operators.find(
            (candidate) =>
                String(candidate.operator_code).toLowerCase() ===
                String(operatorCode).toLowerCase()
        );

        if (!operator) {
            showTost(
                "This biller is no longer available in the current operator API.",
                "error"
            );
            return;
        }

        setSelectedOperator(operator);


        setCustomerNumber(
            String(
                item?.customer_number ||
                item?.customer_no ||
                item?.p1 ||
                ""
            )
        );


        setCustomerEmail(
            String(item?.customer_email || "")
        );
        setAmount(String(item?.amount || ""));

        setPaymentResponse(null);
        setShowForm(true);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <ThemedPageFrame
            activePath="/pay-bills"
            eyebrow="Nabhiverse Payments"
            title="Pay Bills"
            subtitle="Pay your everyday bills securely from one place."
            className="pay_bills_page"
        >
            <div className="pay_bills_shell">
                {error && (
                    <div className="pay_bills_alert">
                        <FiXCircle />
                        <span>{error}</span>

                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                        >
                            <FiRefreshCw />
                            Retry
                        </button>
                    </div>
                )}



                {/* =====================================================
                    PAYMENT FORM
                ====================================================== */}
                {showForm && (
                    <section className="pay_bills_form_section">
                        <div className="pay_bills_form_topbar">
                            <div>
                                <span className="pay_bills_kicker">
                                    BILL PAYMENT
                                </span>

                                <h2>
                                    "Choose a biller"
                                </h2>

                                <p>
                                    Select the biller and enter the details
                                    exactly as shown on your bill.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="pay_bills_back_button"
                                onClick={() => {
                                    setShowForm(false);
                                    resetPaymentForm();
                                }}
                            >
                                Back
                            </button>
                        </div>

                        <div className="pay_bills_main_grid">
                            {/* BILLER LIST */}
                            <div className="pay_bills_biller_panel">
                                <div className="pay_bills_panel_header">
                                    <div>
                                        <strong>Select biller</strong>
                                        <span>
                                            {operators.length} available
                                        </span>
                                    </div>
                                </div>


                                {loading ? (
                                    <div className="pay_bills_operator_grid">
                                        {[1, 2, 3, 4, 5, 6].map((item) => (
                                            <div
                                                className="pay_bills_operator_skeleton"
                                                key={item}
                                            />
                                        ))}
                                    </div>
                                ) : operators.length === 0 ? (
                                    <div className="pay_bills_no_operator">
                                        <FiFileText />
                                        <strong>No billers found</strong>
                                        <span>
                                            No operators were returned by the operator API.
                                        </span>
                                    </div>
                                ) : (
                                    <div className="pay_bills_operator_grid">
                                        {operators.map((operator) => {
                                            const isSelected =
                                                selectedOperator?.operator_code ===
                                                operator.operator_code;

                                            return (
                                                <button
                                                    type="button"
                                                    key={
                                                        operator.id ||
                                                        operator.operator_id ||
                                                        operator.operator_code
                                                    }
                                                    className={`pay_bills_operator_card ${isSelected ? "selected" : ""
                                                        }`}
                                                    onClick={() => handleOperatorSelect(operator)}
                                                >
                                                    <span className="pay_bills_operator_logo">
                                                        {operator.operator_image ? (
                                                            <img
                                                                src={operator.operator_image}
                                                                alt={operator.operator_name}
                                                                className="mobile_recharge_operator_image"
                                                            />
                                                        ) : (
                                                            getInitials(operator.operator_name)
                                                        )}
                                                    </span>

                                                    <span className="pay_bills_operator_info">
                                                        <strong>
                                                            {operator.operator_name}
                                                        </strong>

                                                        <small>
                                                            {operator.operator_code}
                                                        </small>
                                                    </span>

                                                    {isSelected && <FiCheckCircle />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* PAYMENT DETAILS */}
                            <div className="pay_bills_details_panel">
                                {!selectedOperator ? (
                                    <div className="pay_bills_select_placeholder">
                                        <div>
                                            <FiFileText />
                                        </div>

                                        <strong>
                                            Select a biller to continue
                                        </strong>

                                        <span>
                                            Choose a biller from the list to
                                            enter your payment details.
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="pay_bills_selected_operator">
                                            <span className="pay_bills_operator_logo">
                                                {selectedOperator.operator_image ? (
                                                    <img
                                                        src={selectedOperator.operator_image}
                                                        alt={selectedOperator.operator_name}
                                                        className="mobile_recharge_operator_image"
                                                    />
                                                ) : (
                                                    getInitials(selectedOperator.operator_name)
                                                )}
                                            </span>

                                            <div>
                                                <strong>
                                                    {selectedOperator.operator_name}
                                                </strong>

                                                <small>
                                                    Operator code: {selectedOperator.operator_code}
                                                </small>
                                            </div>

                                            <FiCheckCircle />
                                        </div>

                                        <div className="pay_bills_field">
                                            <label>
                                                {getP1Label(selectedOperator) || "P1"}
                                            </label>

                                            <input
                                                value={customerNumber}
                                                onChange={(event) =>
                                                    setCustomerNumber(
                                                        event.target.value
                                                    )
                                                }
                                                placeholder={getP1Placeholder(selectedOperator) || "Enter value"}
                                            />
                                        </div>

                                        <div className="pay_bills_field">
                                            <div className="pay_bills_label_row">
                                                <label>Email address</label>
                                                <span>Required</span>
                                            </div>

                                            <input
                                                type="email"
                                                value={customerEmail}
                                                onChange={(event) =>
                                                    setCustomerEmail(
                                                        event.target.value
                                                    )
                                                }
                                                placeholder="Enter email address"
                                            />
                                        </div>

                                        <div className="pay_bills_field">
                                            <div className="pay_bills_label_row">
                                                <label>Select plan</label>

                                                {selectedOperatorPlans.length > 0 && (
                                                    <span>
                                                        {selectedOperatorPlans.length} plans available
                                                    </span>
                                                )}
                                            </div>

                                            {selectedOperatorPlans.length > 0 ? (
                                                <div className="pay_bills_plan_grid">
                                                    {selectedOperatorPlans.map((plan, index) => (
                                                        <button
                                                            type="button"
                                                            key={`${plan.operator_id}-${plan.amount}-${plan.duration}-${index}`}
                                                            className={`pay_bills_plan_card ${Number(amount) === Number(plan.amount)
                                                                    ? "selected"
                                                                    : ""
                                                                }`}
                                                            onClick={() => {
                                                                setAmount(String(plan.amount));
                                                                setPaymentResponse(null);
                                                            }}
                                                        >
                                                            <div className="pay_bills_plan_top">
                                                                <strong>
                                                                    ₹{plan.amount}
                                                                </strong>

                                                                <span>
                                                                    {plan.duration} Days
                                                                </span>
                                                            </div>

                                                            {plan.description && (
                                                                <p>{plan.description}</p>
                                                            )}

                                                            {plan.channels &&
                                                                Object.keys(plan.channels).length > 0 && (
                                                                    <div className="pay_bills_plan_channels">
                                                                        {Object.values(plan.channels)
                                                                            .slice(0, 4)
                                                                            .map((channel, channelIndex) => (
                                                                                <span key={channelIndex}>
                                                                                    {channel}
                                                                                </span>
                                                                            ))}
                                                                    </div>
                                                                )}
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <input
                                                    type="number"
                                                    inputMode="decimal"
                                                    min="1"
                                                    value={amount}
                                                    onChange={(event) => {
                                                        setAmount(event.target.value);
                                                        setPaymentResponse(null);
                                                    }}
                                                    placeholder="Enter amount"
                                                />
                                            )}
                                        </div>

                                        <div className="pay_bills_security_note">
                                            <FiShield />

                                            <span>
                                                Never share your OTP or payment
                                                PIN. Verify the biller and
                                                account number before paying.
                                            </span>
                                        </div>

                                        {paymentResponse?.status === true && (
                                            <div className="pay_bills_success_box">
                                                <FiCheckCircle />

                                                <div>
                                                    <strong>
                                                        {
                                                            paymentResponse.message
                                                        }
                                                    </strong>

                                                    <span>
                                                        Request ID:{" "}
                                                        {
                                                            paymentResponse.request_id
                                                        }
                                                    </span>

                                                    {paymentResponse
                                                        ?.transaction
                                                        ?.rechargekit_order_id && (
                                                            <span>
                                                                Order ID:{" "}
                                                                {
                                                                    paymentResponse
                                                                        .transaction
                                                                        .rechargekit_order_id
                                                                }
                                                            </span>
                                                        )}

                                                    <span>
                                                        Status:{" "}
                                                        {
                                                            paymentResponse
                                                                ?.transaction
                                                                ?.status
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            className="pay_bills_submit"
                                            disabled={
                                                submitting ||
                                                !selectedOperator ||
                                                !customerNumber ||
                                                !customerEmail ||
                                                !amount
                                            }
                                            onClick={handlePayBill}
                                        >
                                            {submitting ? (
                                                "Processing..."
                                            ) : (
                                                <>
                                                    Pay Bill
                                                    <FiArrowRight />
                                                </>
                                            )}
                                        </button>

                                        <small className="pay_bills_api_note">
                                            Payment requests are sent to the
                                            authenticated Nabhiverse recharge
                                            order API after Razorpay succeeds.
                                        </small>
                                    </>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                {/* =====================================================
                    HISTORY
                ====================================================== */}
                <section className="pay_bills_history_section">
                    <div className="pay_bills_section_header">
                        <div>
                            <span className="pay_bills_kicker">
                                PAYMENT ACTIVITY
                            </span>

                            <h2>Recent Bill Payments</h2>

                            <p>
                                Quickly pay a bill you have used before.
                            </p>
                        </div>

                        {history.length > 5 && (
                            <button
                                type="button"
                                className="pay_bills_view_button"
                                onClick={() =>
                                    setShowAllHistory((current) => !current)
                                }
                            >
                                {showAllHistory
                                    ? "Show less"
                                    : "View all"}
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <div className="pay_bills_history_grid">
                            {[1, 2, 3].map((item) => (
                                <div
                                    className="pay_bills_history_skeleton"
                                    key={item}
                                />
                            ))}
                        </div>
                    ) : visibleHistory.length === 0 ? (
                        <div className="pay_bills_empty_history">
                            <div>
                                <FiFileText />
                            </div>

                            <strong>No bill payments yet</strong>

                            <span>
                                Your bill payment activity will appear here.
                            </span>

                            <button
                                type="button"
                                onClick={handleNewBillPayment}
                            >
                                <FiPlus />
                                Pay your first bill
                            </button>
                        </div>
                    ) : (
                        <div className="pay_bills_history_grid">
                            {visibleHistory.map((item, index) => {
                                const status = getStatus(
                                    item?.status ||
                                    item?.transaction?.status
                                );

                                const statusMeta =
                                    getStatusMeta(status);
                                const historyOperator = getHistoryOperator(item)

                                return (
                                    <article
                                        className="pay_bills_history_card"
                                        key={item?.id || index}
                                    >
                                        <div className="pay_bills_history_top">
                                            <div
                                                className={`pay_bills_history_status ${statusMeta.className}`}
                                            >
                                                {statusMeta.icon}
                                            </div>

                                            <div className="pay_bills_history_info">
                                                <strong>
                                                    {historyOperator?.operator_name ||
                                                        historyOperator?.displayName ||
                                                        "—"}
                                                </strong>

                                                <span>
                                                    {item?.customer_number ??
                                                        item?.customer_no ??
                                                        "—"}
                                                </span>
                                            </div>

                                            <div className="pay_bills_history_amount">
                                                <strong>
                                                    ₹
                                                    {item?.amount ?? "—"}
                                                </strong>

                                                <small>
                                                    {formatDate(
                                                        item?.created_at
                                                    )}
                                                </small>
                                            </div>
                                        </div>

                                        <div className="pay_bills_history_bottom">
                                            <span
                                                className={`pay_bills_history_badge ${statusMeta.className}`}
                                            >
                                                {statusMeta.label}
                                            </span>

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handlePayAgain(item)
                                                }
                                            >
                                                Pay again
                                                <FiArrowRight />
                                            </button>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* =====================================================
                    TRUST FEATURES
                ====================================================== */}
                <section className="pay_bills_trust_grid">
                    <div className="pay_bills_trust_card">
                        <FiLock />
                        <div>
                            <strong>Secure</strong>
                            <span>
                                Token-authenticated requests
                            </span>
                        </div>
                    </div>

                    <div className="pay_bills_trust_card">
                        <FiCheckCircle />
                        <div>
                            <strong>Live billers</strong>
                            <span>
                                Loaded directly from your operator API
                            </span>
                        </div>
                    </div>

                    <div className="pay_bills_trust_card">
                        <FiClock />
                        <div>
                            <strong>Quick payments</strong>
                            <span>
                                Simple payment flow from one place
                            </span>
                        </div>
                    </div>
                </section>
            </div>
        </ThemedPageFrame>
    );
};

export default PayBills;
